import { useCallback, useState } from "react";
import _ from "lodash";

import { useCaching } from "utils/common";
import { handleInvalidRequest } from "utils/sdk";

import Text from "components/Text";
import toast from "components/Toast";
import { useFeatureDisabledDialog } from "components/useFeatureDisabledDialog";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { ECompanySettingsFeatureType } from "entities/Company/components/CompanySettings/constants";
import { useCheckFeature } from "entities/Company/components/CompanySettings/utils";
import {
  companySettingsFeatureLimitCheck,
  useCompanySettings,
} from "entities/Company/sdk";
import DealImportFileUpload from "entities/Deal/components/DealImportFileUpload";
import { dealImportCreate } from "entities/Deal/sdk";
import { upload } from "entities/File/sdk";
import { useBulkDealImportReady } from "entities/TableStorage/hooks";

export interface IAddDealImportButton {
  onCreated: () => void;
}

const AddDealImportButton = ({ onCreated }: IAddDealImportButton) => {
  const { data: companySettings } = useCompanySettings();

  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showLoading, hideLoading] = useLoadingBackdrop();
  const [shouldAddOnboardingChecklist, setShouldAddOnboardingChecklist] =
    useCaching<boolean>(
      companySettings?.onboarding_company_package_enabled || false
    );

  const { show: showFeatureDisabledDialog } = useFeatureDisabledDialog();

  const handleOpen = useCallback(() => {
    companySettingsFeatureLimitCheck({
      featureType: ECompanySettingsFeatureType.MAX_DEAL_COUNT,
    })
      .then(({ has_access }) => {
        if (has_access) {
          companySettingsFeatureLimitCheck({
            featureType: ECompanySettingsFeatureType.BULK_DEAL_CREATION,
          }).then(({ has_access }) => {
            if (!has_access) {
              showFeatureDisabledDialog({});
            } else {
              setOpen(true);
            }
          });
        } else {
          showFeatureDisabledDialog({});
        }
      })
      .catch(handleInvalidRequest);
  }, [showFeatureDisabledDialog]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setOpen(false);
    setShouldAddOnboardingChecklist(
      companySettings?.onboarding_company_package_enabled || false
    );
  }, [companySettings, setShouldAddOnboardingChecklist]);

  const handleOnboardingChecklistCheckboxChange = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: () => setShouldAddOnboardingChecklist((prevValue) => !prevValue),
  });

  const handleDealImportFileSubmit = useCallback(() => {
    if (!_.isEmpty(selectedFile)) {
      showLoading();
      upload({ file: selectedFile, isPrivate: true })
        .then((fileId) => {
          dealImportCreate({ fileId, shouldAddOnboardingChecklist }).then(
            () => {
              toast.successMessage(
                "Deal import has been created successfully, you will be notified once the deals are created."
              );
              handleClose();
            }
          );
        })
        .catch(handleInvalidRequest)
        .finally(hideLoading);
    }
  }, [
    selectedFile,
    showLoading,
    hideLoading,
    handleClose,
    shouldAddOnboardingChecklist,
  ]);

  useBulkDealImportReady({
    successEventHandler: onCreated,
  });

  return (
    <>
      <Text onClick={handleOpen}>Bulk upload deals with Excel</Text>
      <DealImportFileUpload
        selectedFile={selectedFile}
        handleFileSelect={setSelectedFile}
        handleDealImportFileSubmit={handleDealImportFileSubmit}
        open={open}
        handleClose={handleClose}
        shouldAddOnboardingChecklist={shouldAddOnboardingChecklist}
        handleOnboardingChecklistCheckboxChange={
          handleOnboardingChecklistCheckboxChange
        }
      />
    </>
  );
};

export default AddDealImportButton;
